import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { can } from "@/lib/permissions";
import { PageHeader, EmptyState, StatusPill } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { HardDrives, Plus, Star, PlugsConnected, UsersThree, Power, PencilSimple, Trash, ArrowLeft, ArrowsLeftRight } from "@phosphor-icons/react";
import ServerReassignDeleteDialog from "@/components/ServerReassignDeleteDialog";
import ServerPoolsPanel from "@/components/ServerPoolsPanel";
import MoveServerPoolDialog from "@/components/MoveServerPoolDialog";

const EMPTY = {
  name: "", host: "", api_base_url: "", environment: "dev",
  call_limit: "200", assignment_enabled: true, status: "active",
  cred_username: "", cred_secret: "", notes: "", pool_id: "",
};

const HEALTH_CLS = {
  healthy: "text-emerald-600",
  degraded: "text-amber-600",
  unreachable: "text-destructive",
  disabled: "text-muted-foreground",
  maintenance: "text-amber-600",
  configured: "text-muted-foreground",
  unknown: "text-muted-foreground",
};

export default function PdServers() {
  const { user } = useAuth();
  const canAdd = can(user, "servers-pd", "add");
  const canEdit = can(user, "servers-pd", "edit");
  const canDelete = can(user, "servers-pd", "delete");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(null);
  const [assignDlg, setAssignDlg] = useState(null);
  const [assignData, setAssignData] = useState({ customers: [], resellers: [] });
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleteDeps, setDeleteDeps] = useState(null);
  const [deleteDepsLoading, setDeleteDepsLoading] = useState(false);
  const [pools, setPools] = useState([]);
  const [activePool, setActivePool] = useState(null);
  const [moveTarget, setMoveTarget] = useState(null);
  const [moveDeps, setMoveDeps] = useState(null);
  const [moveDepsLoading, setMoveDepsLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows((await api.get("/servers/pd")).data); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const poolRows = activePool ? rows.filter((s) => s.pool_id === activePool.id) : [];

  const openNew = () => {
    setEdit(null);
    const def = activePool || pools.find((p) => p.is_default) || pools[0];
    setForm({ ...EMPTY, pool_id: def?.id || "" });
    setOpen(true);
  };
  const openEdit = (s) => {
    setEdit(s);
    setForm({
      name: s.name, host: s.host, api_base_url: s.api_base_url || "",
      environment: s.environment || "dev",
      call_limit: String(s.call_limit ?? 200),
      assignment_enabled: s.assignment_enabled !== false,
      status: s.status || "active",
      cred_username: s.cred_username || "", cred_secret: "",
      notes: s.notes || "",
      pool_id: s.pool_id || "",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim() || !form.host.trim()) {
      toast.error("Name and host are required"); return;
    }
    const limit = Number(form.call_limit);
    if (!Number.isInteger(limit) || limit < 0) {
      toast.error("Call limit must be 0 or a positive whole number"); return;
    }
    setSaving(true);
    try {
      const body = {
        name: form.name.trim(), host: form.host.trim(),
        api_base_url: form.api_base_url.trim() || null,
        environment: form.environment,
        call_limit: limit,
        assignment_enabled: !!form.assignment_enabled,
        status: form.status,
        cred_username: form.cred_username || null,
        pool_id: form.pool_id || null,
        notes: form.notes || null,
      };
      if (form.cred_secret) body.cred_secret = form.cred_secret;
      if (edit) await api.patch(`/servers/pd/${edit.id}`, body);
      else await api.post("/servers/pd", body);
      toast.success(edit ? "PD server updated" : "PD server added");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const act = async (fn, id) => {
    setBusy(id);
    try { await fn(); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };
  const openDelete = async (s) => {
    setConfirmDelete(s);
    setDeleteDeps(null);
    setDeleteDepsLoading(true);
    try {
      setDeleteDeps((await api.get(`/servers/pd/${s.id}/customers`)).data);
    } catch {
      setDeleteDeps({ customers: [], resellers: [], campaigns: [] });
    } finally {
      setDeleteDepsLoading(false);
    }
  };
  const del = async (reassignTo) => {
    const s = confirmDelete;
    if (!s) return;
    setConfirmDelete(null);
    await act(async () => {
      const params = reassignTo ? { reassign_to: reassignTo } : undefined;
      await api.delete(`/servers/pd/${s.id}`, { params });
      toast.success(reassignTo ? "Reassigned and removed" : "PD server removed");
    }, s.id);
  };
  const toggleStatus = (s) => act(async () => {
    const next = s.status === "active" ? "inactive" : "active";
    await api.post(`/servers/pd/${s.id}/status`, { status: next });
    toast.success("Status updated");
  }, s.id);
  const setDefault = (s) => act(async () => {
    await api.post(`/servers/pd/${s.id}/default`, { is_default: !s.is_default });
    toast.success(s.is_default ? "Default cleared" : "Set as default");
  }, s.id);
  const test = (s) => act(async () => {
    const { data } = await api.post(`/servers/pd/${s.id}/test`);
    if (data.ok) toast.success(`${data.health} · ${data.latency_ms ?? "—"} ms`);
    else toast.error(data.message || data.health || "Health check failed");
  }, s.id);
  const viewAssigned = async (s) => {
    setAssignDlg(s);
    try { setAssignData((await api.get(`/servers/pd/${s.id}/customers`)).data); }
    catch { setAssignData({ customers: [], resellers: [] }); }
  };
  const openMove = async (s) => {
    setMoveTarget(s);
    setMoveDeps(null);
    setMoveDepsLoading(true);
    try {
      setMoveDeps((await api.get(`/servers/pd/${s.id}/customers`)).data);
    } catch {
      setMoveDeps({ customers: [], resellers: [], campaigns: [] });
    } finally {
      setMoveDepsLoading(false);
    }
  };
  const moveServer = async (poolId, reassignTo) => {
    const s = moveTarget;
    if (!s || !poolId) return;
    await act(async () => {
      await api.post("/servers/pools/move-server", {
        kind: "pd", server_id: s.id, pool_id: poolId,
        reassign_to: reassignTo || null,
      });
      toast.success(reassignTo ? "Reassigned and moved" : "Server moved");
      setMoveTarget(null);
    }, s.id);
  };

  return (
    <div>
      <PageHeader
        title={activePool ? `PD · ${activePool.name}` : "PD Servers"}
        subtitle={activePool ? "Servers in this pool" : "Predictive dialing servers grouped by pool — open a pool to manage servers"}
      >
        {activePool && (
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setActivePool(null)} data-testid="pd-back-pools" className="rounded-lg">
              <ArrowLeft className="w-4 h-4 mr-1.5" /> Pools
            </Button>
            {canAdd && (
              <Button onClick={openNew} data-testid="create-pd-btn" className="rounded-lg">
                <Plus className="w-4 h-4 mr-1.5" weight="bold" /> Add PD Server
              </Button>
            )}
          </div>
        )}
      </PageHeader>

      {!activePool ? (
        <ServerPoolsPanel
          kind="pd"
          canAdd={canAdd}
          canEdit={canEdit}
          canDelete={canDelete}
          onPoolsChange={setPools}
          onOpenPool={setActivePool}
          testIdPrefix="pd-pool"
        />
      ) : loading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : poolRows.length === 0 ? (
        <EmptyState testid="pd-empty" icon={HardDrives} title="No servers in this pool"
          description="Register a PD server in this pool so you can assign it to accounts." />
      ) : (
          <div className="rounded-xl border border-border bg-card card-shadow overflow-hidden" data-testid="pd-table">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Name</TableHead><TableHead>Host</TableHead><TableHead>API URL</TableHead>
                <TableHead>Env</TableHead><TableHead className="text-right">Call limit</TableHead>
                <TableHead>Health</TableHead><TableHead>Default</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {poolRows.map((s) => (
                  <TableRow key={s.id} className="hover:bg-muted/40" data-testid={`pd-row-${s.id}`}>
                    <TableCell className="font-medium">{s.name}</TableCell>
                    <TableCell className="font-mono text-sm">{s.host}</TableCell>
                    <TableCell className="font-mono text-xs max-w-[180px] truncate" title={s.api_base_url || ""}>
                      {s.api_base_url || "—"}
                    </TableCell>
                    <TableCell className="text-sm">{s.environment || "dev"}</TableCell>
                    <TableCell className="text-right tabular-nums text-sm" data-testid={`pd-call-limit-${s.id}`}>
                      {s.call_limit === 0 ? "Unlimited" : (s.call_limit ?? "—")}
                    </TableCell>
                    <TableCell>
                      <span className={`text-xs font-medium capitalize ${HEALTH_CLS[s.health] || HEALTH_CLS.unknown}`}
                        data-testid={`pd-health-${s.id}`}>
                        {s.health || "unknown"}
                      </span>
                    </TableCell>
                    <TableCell>
                      {canEdit ? (
                        <button onClick={() => setDefault(s)} disabled={busy === s.id} data-testid={`pd-default-${s.id}`}
                          className={`inline-flex items-center gap-1 text-xs ${s.is_default ? "text-amber-500" : "text-muted-foreground hover:text-foreground"}`}>
                          <Star className="w-4 h-4" weight={s.is_default ? "fill" : "regular"} /> {s.is_default ? "Default" : "Set"}
                        </button>
                      ) : (
                        s.is_default ? <span className="text-xs text-amber-500">Default</span> : <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell><StatusPill status={s.status} /></TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-1">
                        {canEdit && (
                          <Button variant="ghost" size="sm" className="rounded-lg px-2" onClick={() => test(s)} disabled={busy === s.id} data-testid={`pd-test-${s.id}`} title="Health check"><PlugsConnected className="w-4 h-4" /></Button>
                        )}
                        <Button variant="ghost" size="sm" className="rounded-lg px-2" onClick={() => viewAssigned(s)} data-testid={`pd-customers-${s.id}`} title="Assigned accounts"><UsersThree className="w-4 h-4" /></Button>
                        {canEdit && (
                          <Button variant="ghost" size="sm" className="rounded-lg px-2" onClick={() => toggleStatus(s)} disabled={busy === s.id} data-testid={`pd-toggle-${s.id}`} title={s.status === "active" ? "Deactivate" : "Activate"}><Power className="w-4 h-4" /></Button>
                        )}
                        {canEdit && (
                          <Button variant="ghost" size="sm" className="rounded-lg px-2" onClick={() => openMove(s)} disabled={busy === s.id} data-testid={`move-pd-${s.id}`} title="Move to another pool" aria-label="Move"><ArrowsLeftRight className="w-4 h-4" /></Button>
                        )}
                        {canEdit && (
                          <Button variant="outline" size="sm" className="rounded-lg h-8 w-8 p-0" onClick={() => openEdit(s)} data-testid={`edit-pd-${s.id}`} title="Edit" aria-label="Edit"><PencilSimple className="w-4 h-4" /></Button>
                        )}
                        {canDelete && (
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-destructive hover:text-destructive" onClick={() => openDelete(s)} data-testid={`delete-pd-${s.id}`} title="Delete" aria-label="Delete"><Trash className="w-4 h-4" /></Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="pd-dialog">
          <DialogHeader>
            <DialogTitle>{edit ? "Edit" : "Add"} PD server</DialogTitle>
            <DialogDescription>
              Predictive dialing service host. Keep this separate from the AI Engine — do not reuse AI Engine ports or processes.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto pr-1">
            <div className="space-y-2"><Label>Name</Label><Input data-testid="pd-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="space-y-2"><Label>Pool</Label>
              <Select value={form.pool_id || ""} onValueChange={(v) => setForm({ ...form, pool_id: v })}>
                <SelectTrigger data-testid="pd-form-pool"><SelectValue placeholder="Select pool" /></SelectTrigger>
                <SelectContent>
                  {pools.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}{p.is_default ? " (default)" : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Host / IP</Label><Input data-testid="pd-host" value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })} placeholder="157.90.96.61" /></div>
            <div className="space-y-2"><Label>API base URL</Label>
              <Input data-testid="pd-api-url" value={form.api_base_url} onChange={(e) => setForm({ ...form, api_base_url: e.target.value })} placeholder="http://127.0.0.1:9400" />
              <p className="text-xs text-muted-foreground">Set after the Python PD service is deployed. Health checks call GET /health on this URL.</p>
            </div>
            <div className="space-y-2"><Label>Call limit</Label>
              <Input data-testid="pd-call-limit" inputMode="numeric" value={form.call_limit}
                onChange={(e) => setForm({ ...form, call_limit: e.target.value })} placeholder="200" />
              <p className="text-xs text-muted-foreground">
                Simultaneous calls this PD server may run across all campaigns. Use 0 for
                unlimited. Campaigns are not capped individually — the engine picks this up
                within a few seconds, no restart needed.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Environment</Label>
                <Select value={form.environment} onValueChange={(v) => setForm({ ...form, environment: v })}>
                  <SelectTrigger data-testid="pd-environment"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dev">dev</SelectItem>
                    <SelectItem value="staging">staging</SelectItem>
                    <SelectItem value="production">production</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="pd-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">active</SelectItem>
                    <SelectItem value="inactive">inactive</SelectItem>
                    <SelectItem value="maintenance">maintenance</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!form.assignment_enabled} data-testid="pd-assignment-enabled"
                onChange={(e) => setForm({ ...form, assignment_enabled: e.target.checked })} />
              Allow new assignments
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>API username <span className="text-muted-foreground">(optional)</span></Label>
                <Input data-testid="pd-cred-user" value={form.cred_username} onChange={(e) => setForm({ ...form, cred_username: e.target.value })} /></div>
              <div className="space-y-2"><Label>API secret <span className="text-muted-foreground">(optional)</span></Label>
                <Input data-testid="pd-cred-secret" type="password" value={form.cred_secret} onChange={(e) => setForm({ ...form, cred_secret: e.target.value })} placeholder={edit?.has_cred_secret ? "•••••• (leave blank to keep)" : ""} /></div>
            </div>
            <div className="space-y-2"><Label>Notes</Label>
              <Input data-testid="pd-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button onClick={save} disabled={saving} data-testid="submit-pd-btn" className="rounded-lg">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!assignDlg} onOpenChange={(v) => !v && setAssignDlg(null)}>
        <DialogContent data-testid="pd-assigned-dialog">
          <DialogHeader>
            <DialogTitle>Accounts using {assignDlg?.name}</DialogTitle>
            <DialogDescription>Resellers and customers currently assigned this PD server.</DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-4 max-h-[50vh] overflow-y-auto">
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-2">Resellers</p>
              {(assignData.resellers || []).length === 0 && <p className="text-sm text-muted-foreground">None</p>}
              {(assignData.resellers || []).map((c) => (
                <div key={c.id} className="flex items-center justify-between rounded-lg border border-border p-3 mb-2">
                  <div><p className="font-medium text-sm">{c.name}</p><p className="text-xs text-muted-foreground">{c.company || "—"}</p></div>
                  <StatusPill status={c.status} />
                </div>
              ))}
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-2">Customers</p>
              {(assignData.customers || []).length === 0 && <p className="text-sm text-muted-foreground">None</p>}
              {(assignData.customers || []).map((c) => (
                <div key={c.id} className="flex items-center justify-between rounded-lg border border-border p-3 mb-2" data-testid={`pd-cust-${c.id}`}>
                  <div><p className="font-medium text-sm">{c.name}</p><p className="text-xs text-muted-foreground">{c.company || "—"}</p></div>
                  <StatusPill status={c.status} />
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ServerReassignDeleteDialog
        open={!!confirmDelete}
        onOpenChange={(v) => !v && setConfirmDelete(null)}
        server={confirmDelete}
        serverLabel="PD server"
        alternatives={rows.filter((r) => r.status === "active")}
        dependents={deleteDeps}
        loadingDependents={deleteDepsLoading}
        busy={busy === confirmDelete?.id}
        onConfirm={del}
        testIdPrefix="pd"
      />

      <MoveServerPoolDialog
        open={!!moveTarget}
        onOpenChange={(v) => !v && setMoveTarget(null)}
        server={moveTarget}
        pools={pools}
        currentPoolId={activePool?.id || moveTarget?.pool_id}
        samePoolServers={rows.filter((r) =>
          r.pool_id === (activePool?.id || moveTarget?.pool_id)
          && r.status === "active" && r.assignment_enabled !== false)}
        dependents={moveDeps}
        loadingDependents={moveDepsLoading}
        busy={busy === moveTarget?.id}
        onConfirm={moveServer}
        testIdPrefix="pd-move"
        serverLabel="PD server"
      />
    </div>
  );
}
